import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { LoadingState, ConfirmModal } from '../../../components/admin/shared';
import { Save, AlertCircle, CheckCircle, Settings } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const SystemConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errors, setErrors] = useState({});

  const [config, setConfig] = useState({
    maxFileUploadMB: 10,
    allowedFileTypes: ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'],
    maintenanceMode: false,
    maintenanceMessage: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: 'Educential',
    notificationEmail: '',
    enableEmailNotifications: true,
    sessionTimeoutMinutes: 60,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requirePasswordSpecialChars: true
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get('/api/admin/settings/system');

      // Merge fetched config with defaults
      const fetchedConfig = response.data.reduce((acc, item) => {
        acc[item.key] = item.value;
        return acc;
      }, {});

      setConfig({ ...config, ...fetchedConfig });
    } catch (error) {
      console.error('Error loading system config:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateConfig = () => {
    const newErrors = {};

    if (config.maxFileUploadMB < 1 || config.maxFileUploadMB > 100) {
      newErrors.maxFileUploadMB = 'Must be between 1 and 100 MB';
    }

    if (config.allowedFileTypes.length === 0) {
      newErrors.allowedFileTypes = 'At least one file type must be allowed';
    }

    if (config.maintenanceMode && !config.maintenanceMessage.trim()) {
      newErrors.maintenanceMessage = 'Maintenance message is required when maintenance mode is enabled';
    }

    if (config.smtpHost && !config.smtpUser) {
      newErrors.smtpUser = 'SMTP user is required when SMTP host is set';
    }

    if (config.smtpHost && !config.smtpFromEmail) {
      newErrors.smtpFromEmail = 'From email is required when SMTP is configured';
    }

    if (config.sessionTimeoutMinutes < 5 || config.sessionTimeoutMinutes > 1440) {
      newErrors.sessionTimeoutMinutes = 'Must be between 5 and 1440 minutes';
    }

    if (config.passwordMinLength < 6 || config.passwordMinLength > 32) {
      newErrors.passwordMinLength = 'Must be between 6 and 32 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateConfig()) {
      return;
    }

    setShowConfirmModal(false);
    setSaving(true);

    try {
      // Convert config object to array of key-value pairs
      const configArray = Object.entries(config).map(([key, value]) => ({
        key,
        value
      }));

      await educentialApi.patch('/api/admin/settings/system', { settings: configArray });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving system config:', error);
      alert('Failed to save system configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileTypeToggle = (fileType) => {
    setConfig(prev => ({
      ...prev,
      allowedFileTypes: prev.allowedFileTypes.includes(fileType)
        ? prev.allowedFileTypes.filter(t => t !== fileType)
        : [...prev.allowedFileTypes, fileType]
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="System Configuration">
        <LoadingState message="Loading system configuration..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="System Configuration">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">System Configuration</h2>
            <p className="text-gray-600 mt-1">Manage platform-wide settings and configuration</p>
          </div>
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-green-900 font-medium">Configuration saved successfully!</p>
              <p className="text-green-700 text-sm">Your changes have been applied to the system.</p>
            </div>
          </div>
        )}

        {/* File Upload Settings */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>File Upload Settings</span>
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum File Upload Size (MB)
            </label>
            <input
              type="number"
              value={config.maxFileUploadMB}
              onChange={(e) => setConfig({ ...config, maxFileUploadMB: parseInt(e.target.value) || 0 })}
              className={`w-full md:w-64 border rounded-lg px-4 py-2 ${
                errors.maxFileUploadMB ? 'border-red-500' : 'border-gray-300'
              }`}
              min="1"
              max="100"
            />
            {errors.maxFileUploadMB && (
              <p className="text-red-500 text-sm mt-1">{errors.maxFileUploadMB}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed File Types
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'].map((fileType) => (
                <label
                  key={fileType}
                  className={`flex items-center space-x-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    config.allowedFileTypes.includes(fileType)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={config.allowedFileTypes.includes(fileType)}
                    onChange={() => handleFileTypeToggle(fileType)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-900 uppercase">{fileType}</span>
                </label>
              ))}
            </div>
            {errors.allowedFileTypes && (
              <p className="text-red-500 text-sm mt-2">{errors.allowedFileTypes}</p>
            )}
          </div>
        </div>

        {/* Maintenance Mode */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Maintenance Mode</h3>

          <label className="flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={config.maintenanceMode}
              onChange={(e) => setConfig({ ...config, maintenanceMode: e.target.checked })}
              className="mt-1"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Enable Maintenance Mode</span>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, users will see a maintenance message and won't be able to access the platform
              </p>
            </div>
          </label>

          {config.maintenanceMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maintenance Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={config.maintenanceMessage}
                onChange={(e) => setConfig({ ...config, maintenanceMessage: e.target.value })}
                rows={4}
                className={`w-full border rounded-lg px-4 py-2 ${
                  errors.maintenanceMessage ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="We're currently performing scheduled maintenance. We'll be back soon!"
              />
              {errors.maintenanceMessage && (
                <p className="text-red-500 text-sm mt-1">{errors.maintenanceMessage}</p>
              )}
            </div>
          )}
        </div>

        {/* SMTP Settings */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">SMTP Email Configuration</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
              <input
                type="text"
                value={config.smtpHost}
                onChange={(e) => setConfig({ ...config, smtpHost: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                placeholder="smtp.example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
              <input
                type="number"
                value={config.smtpPort}
                onChange={(e) => setConfig({ ...config, smtpPort: parseInt(e.target.value) || 587 })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username</label>
              <input
                type="text"
                value={config.smtpUser}
                onChange={(e) => setConfig({ ...config, smtpUser: e.target.value })}
                className={`w-full border rounded-lg px-4 py-2 ${
                  errors.smtpUser ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="user@example.com"
              />
              {errors.smtpUser && <p className="text-red-500 text-sm mt-1">{errors.smtpUser}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
              <input
                type="password"
                value={config.smtpPassword}
                onChange={(e) => setConfig({ ...config, smtpPassword: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
              <input
                type="email"
                value={config.smtpFromEmail}
                onChange={(e) => setConfig({ ...config, smtpFromEmail: e.target.value })}
                className={`w-full border rounded-lg px-4 py-2 ${
                  errors.smtpFromEmail ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="noreply@educential.com"
              />
              {errors.smtpFromEmail && <p className="text-red-500 text-sm mt-1">{errors.smtpFromEmail}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
              <input
                type="text"
                value={config.smtpFromName}
                onChange={(e) => setConfig({ ...config, smtpFromName: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                placeholder="Educential"
              />
            </div>
          </div>

          <label className="flex items-center space-x-2 mt-4">
            <input
              type="checkbox"
              checked={config.enableEmailNotifications}
              onChange={(e) => setConfig({ ...config, enableEmailNotifications: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Enable email notifications</span>
          </label>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Security Settings</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                value={config.sessionTimeoutMinutes}
                onChange={(e) => setConfig({ ...config, sessionTimeoutMinutes: parseInt(e.target.value) || 60 })}
                className={`w-full border rounded-lg px-4 py-2 ${
                  errors.sessionTimeoutMinutes ? 'border-red-500' : 'border-gray-300'
                }`}
                min="5"
                max="1440"
              />
              {errors.sessionTimeoutMinutes && (
                <p className="text-red-500 text-sm mt-1">{errors.sessionTimeoutMinutes}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Login Attempts
              </label>
              <input
                type="number"
                value={config.maxLoginAttempts}
                onChange={(e) => setConfig({ ...config, maxLoginAttempts: parseInt(e.target.value) || 5 })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                min="3"
                max="10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Password Length
              </label>
              <input
                type="number"
                value={config.passwordMinLength}
                onChange={(e) => setConfig({ ...config, passwordMinLength: parseInt(e.target.value) || 8 })}
                className={`w-full border rounded-lg px-4 py-2 ${
                  errors.passwordMinLength ? 'border-red-500' : 'border-gray-300'
                }`}
                min="6"
                max="32"
              />
              {errors.passwordMinLength && (
                <p className="text-red-500 text-sm mt-1">{errors.passwordMinLength}</p>
              )}
            </div>

            <div className="flex items-center pt-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config.requirePasswordSpecialChars}
                  onChange={(e) => setConfig({ ...config, requirePasswordSpecialChars: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Require special characters in passwords</span>
              </label>
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Important</p>
              <p className="text-sm text-yellow-800 mt-1">
                Changes to system configuration will affect all users. Please ensure you test critical
                settings like SMTP configuration before enabling email notifications.
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={showConfirmModal}
          title="Save System Configuration?"
          message="Are you sure you want to save these system configuration changes? This will affect all users and platform functionality."
          confirmText="Save Changes"
          cancelText="Cancel"
          onConfirm={handleSave}
          onCancel={() => setShowConfirmModal(false)}
        />
      </div>
    </AdminLayout>
  );
};

export default SystemConfig;
